import axios from "axios";
import { getStoredAuthToken } from "../utils/authStorage";

const KNOWN_PRODUCTION_API_URL = "https://task-manager-8vth.onrender.com";
const MANUAL_LOGOUT_FLAG = "task-suite:manual-logout";
const MANUAL_LOGOUT_GRACE_MS = 10000;
const GUEST_DEVICE_ID_KEY = "task-suite:guest-device-id";

const normalizeOrigin = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.replace(/\/+$/, "");
};

const getGuestDeviceId = () => {
  if (typeof window === "undefined") return "";
  let value = window.localStorage.getItem(GUEST_DEVICE_ID_KEY);
  if (value) return value;
  try { value = window.crypto?.randomUUID?.(); } catch { value = ""; }
  if (!value) value = `gd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  window.localStorage.setItem(GUEST_DEVICE_ID_KEY, value);
  return value;
};

export const getApiBaseUrl = () => {
  const configured = normalizeOrigin(process.env.REACT_APP_API_URL);
  if (configured) return configured;
  if (typeof window === "undefined") return "";
  const { protocol, hostname, port, origin } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1" || port === "3000") return `${protocol}//${hostname}:5000`;
  if (hostname.endsWith(".vercel.app") || hostname === "tasksuite.co" || hostname === "www.tasksuite.co") return KNOWN_PRODUCTION_API_URL;
  return origin;
};

export const buildApiUrl = (path = "") => {
  const baseUrl = getApiBaseUrl();
  if (!path) return baseUrl;
  if (/^https?:\/\//i.test(path)) return path;
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true, timeout: 20000 });

api.interceptors.request.use(
  (config) => {
    const token = getStoredAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    const guestDeviceId = getGuestDeviceId();
    if (guestDeviceId) config.headers["X-Guest-Device-ID"] = guestDeviceId;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const isNetworkFailure = !error.response || error.code === "ECONNABORTED" || error.message?.includes("Network Error");
    const shouldRedirect =
      (status === 401 || status === 403) &&
      !isNetworkFailure &&
      !error.config?.skipAuthRedirect &&
      !/\/api\/auth\/(login|register)$/i.test(error.config?.url || "");

    if (shouldRedirect) {
      if (typeof window !== "undefined") {
        const logoutTimestamp = Number(window.sessionStorage.getItem(MANUAL_LOGOUT_FLAG) || 0);
        if (logoutTimestamp) {
          const isRecentManualLogout = Date.now() - logoutTimestamp < MANUAL_LOGOUT_GRACE_MS;
          if (isRecentManualLogout) {
            if (window.location.pathname !== "/") window.location.replace("/");
            return Promise.reject(error);
          }
          window.sessionStorage.removeItem(MANUAL_LOGOUT_FLAG);
        }
        window.dispatchEvent(new CustomEvent("task-suite:auth-rejected", { detail: { status } }));
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post("/api/auth/register", data, { skipAuthRedirect: true }),
  login: (data) => api.post("/api/auth/login", data, { skipAuthRedirect: true }),
  guestLogin: () => api.post("/api/auth/guest", {}, { skipAuthRedirect: true, timeout: 25000 }),
  getProfile: (config = {}) => api.get("/api/auth/profile", config),
  getPublicProfile: (userId) => api.get(`/api/auth/profile/${userId}/public`),
  getPublicProfiles: (userIds) => api.post("/api/auth/profiles/public", { userIds }, { skipAuthRedirect: true }),
  uploadProfilePhoto: (imageData) => api.put("/api/auth/profile/photo", { imageData }),
};

export const tasksAPI = {
  getTasks: () => api.get("/api/tasks"),
  getStats: () => api.get("/api/tasks/stats"),
  createTask: (data) => api.post("/api/tasks", data),
  updateTask: (id, data) => api.put(`/api/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/api/tasks/${id}`),
};

export const teamsAPI = {
  getTeams: () => api.get("/api/teams/my"),
  createTeam: (data) => api.post("/api/teams", data),
  getTeam: (teamId) => api.get(`/api/teams/${teamId}/details`),
  getAttendance: (teamId, params = {}) => api.get(`/api/teams/${teamId}/attendance`, { params }),
  punchInAttendance: (teamId, data = {}) => api.post(`/api/teams/${teamId}/attendance/punch-in`, data),
  heartbeatAttendance: (teamId, data = {}) => api.post(`/api/teams/${teamId}/attendance/heartbeat`, data),
  punchOutAttendance: (teamId, data = {}) => api.post(`/api/teams/${teamId}/attendance/punch-out`, data),
  updateTeam: (teamId, data) => api.put(`/api/teams/${teamId}`, data),
  deleteTeam: (teamId) => api.delete(`/api/teams/${teamId}`),
  joinTeam: (teamId) => api.post(`/api/teams/${teamId}/join`),
  getMyJoinRequests: () => api.get("/api/teams/join-requests/mine"),
  getJoinRequests: (teamId) => api.get(`/api/teams/${teamId}/join-requests`),
  retractJoinRequest: (teamId) => api.post(`/api/teams/${teamId}/join/retract`),
  clearRejectedJoinRequest: (notificationId) => api.delete(`/api/teams/join-requests/rejected/${notificationId}`),
  leaveTeam: (teamId) => api.post(`/api/teams/${teamId}/leave`),
  getInviteLink: (teamId) => api.get(`/api/teams/${teamId}/invite`),
  approveJoinRequest: (teamId, userId) => api.post(`/api/teams/${teamId}/join-requests/${userId}/approve`),
  rejectJoinRequest: (teamId, userId) => api.post(`/api/teams/${teamId}/join-requests/${userId}/reject`),
  updateMemberRole: (teamId, userId, role) => api.put(`/api/teams/${teamId}/members/${userId}/role`, { role }),
  updateMemberDisplayName: (teamId, userId, teamDisplayName) => api.put(`/api/teams/${teamId}/members/${userId}/display-name`, { teamDisplayName }),
  getTaskViews: (teamId) => api.get(`/api/teams/${teamId}/task-views`),
  createTaskView: (teamId, data) => api.post(`/api/teams/${teamId}/task-views`, data),
  updateTaskView: (teamId, viewId, data) => api.put(`/api/teams/${teamId}/task-views/${viewId}`, data),
  deleteTaskView: (teamId, viewId) => api.delete(`/api/teams/${teamId}/task-views/${viewId}`),
  removeMember: (teamId, userId) => api.delete(`/api/teams/${teamId}/members/${userId}`),
  getTeamRoles: (teamId) => api.get(`/api/teams/${teamId}/roles`),
  createRole: (teamId, data) => api.post(`/api/teams/${teamId}/roles`, data),
  updateRole: (teamId, roleName, data) => api.put(`/api/teams/${teamId}/roles/${roleName}`, data),
  deleteRole: (teamId, roleName, data) => api.delete(`/api/teams/${teamId}/roles/${roleName}`, { data }),
  getAvailablePowers: (teamId) => api.get(`/api/teams/${teamId}/powers`),
  updateIgeSettings: (teamId, enabled) => api.patch(`/api/teams/${teamId}/ige-settings`, { enabled }),
};

export const projectsAPI = {
  getTeamProjects: (teamId) => api.get(`/api/projects/${teamId}`),
  createProject: (teamId, data) => api.post(`/api/projects/${teamId}`, data),
  updateProject: (projectId, data) => api.put(`/api/projects/${projectId}`, data),
  deleteProject: (projectId) => api.delete(`/api/projects/${projectId}`),
};

export const teamTasksAPI = {
  getMyTeamTasks: () => api.get("/api/team-tasks/my/all"),
  getTeamTasks: (teamId, filters = {}) => api.get(`/api/team-tasks/${teamId}`, { params: filters }),
  createTask: (teamId, data) => api.post(`/api/team-tasks/${teamId}`, data),
  updateTask: (taskId, data) => api.put(`/api/team-tasks/${taskId}`, data),
  deleteTask: (taskId) => api.delete(`/api/team-tasks/${taskId}`),
  requestExtension: (taskId, payload) => api.post(`/api/team-tasks/${taskId}/request-extension`, payload),
  approveExtension: (taskId) => api.post(`/api/team-tasks/${taskId}/extension/approve`),
  rejectExtension: (taskId) => api.post(`/api/team-tasks/${taskId}/extension/reject`),
  getPendingExtensions: (teamId) => api.get(`/api/team-tasks/${teamId}/extensions/pending`),
};

export const notificationsAPI = {
  getNotifications: () => api.get("/api/notifications"),
  markAsRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.put("/api/notifications/read-all"),
  deleteNotification: (id) => api.delete(`/api/notifications/${id}`),
  clearAll: () => api.delete("/api/notifications"),
  getPushVapidPublicKey: () => api.get("/api/notifications/push/vapid-public-key"),
  subscribePush: (subscription) => api.post("/api/notifications/push/subscribe", { subscription }),
  unsubscribePush: (endpoint) => api.post("/api/notifications/push/unsubscribe", { endpoint }),
};

export const billingAPI = {
  getPlans: () => api.get("/api/billing/plans"),
  getBilling: () => api.get("/api/billing/me"),
  startTrial: (planCode) => api.post("/api/billing/start-trial", { planCode }),
  createCheckoutOrder: (planCode) => api.post("/api/billing/checkout/order", { planCode }),
  verifyCheckout: (payload) => api.post("/api/billing/checkout/verify", payload),
  previewTeamCheckout: (data) => api.post("/api/billing/team-checkout/preview", data),
  createTeamCheckoutOrder: (data) => api.post("/api/billing/team-checkout/order", data),
  verifyTeamCheckout: (payload) => api.post("/api/billing/team-checkout/verify", payload),
  createSubscription: (data) => api.post("/api/billing/subscriptions/create", data),
  verifySubscription: (payload) => api.post("/api/billing/subscriptions/verify", payload),
};

export const contactAPI = {
  submit: (data) => api.post("/api/contact", data, { skipAuthRedirect: true }),
};

export const commentsAPI = {
  getByTask: (taskId) => api.get(`/api/comments/${taskId}`),
  create: (taskId, data) => api.post(`/api/comments/${taskId}`, data),
  delete: (commentId) => api.delete(`/api/comments/${commentId}`),
};

export const journalsAPI = {
  getEntries: (params = {}) => api.get("/api/journals", { params }),
  getEntry: (id) => api.get(`/api/journals/${id}`),
  createEntry: (data) => api.post("/api/journals", data),
  updateEntry: (id, data) => api.put(`/api/journals/${id}`, data),
  toggleFavorite: (id) => api.patch(`/api/journals/${id}/favorite`),
  deleteEntry: (id) => api.delete(`/api/journals/${id}`),
  getInsights: (days = 30) => api.get("/api/journals/insights/summary", { params: { days } }),
  getReminderSettings: () => api.get("/api/journals/reminder-settings"),
  updateReminderSettings: (data) => api.put("/api/journals/reminder-settings", data),
  emitReminderNotification: () => api.post("/api/journals/reminder-notify"),
};

export const calendarAPI = {
  getItems: (params = {}) => api.get("/api/calendar", { params }),
  createItem: (data) => api.post("/api/calendar", data),
  deleteItem: (id) => api.delete(`/api/calendar/${id}`),
};

export const searchAPI = {
  search: (query, limit = 24) => api.get("/api/search", { params: { q: query, limit } }),
};

export const chatAPI = {
  getChannels: (teamId) => api.get(`/api/chat/channels/${teamId}`, { timeout: 30000 }),
  createChannel: (teamId, data) => api.post(`/api/chat/channels/${teamId}`, data, { timeout: 30000 }),
  getMessages: (channelId, params = {}) => api.get(`/api/chat/channels/${channelId}/messages`, { params, timeout: 30000 }),
  sendMessage: (channelId, data) => api.post(`/api/chat/channels/${channelId}/messages`, data, { timeout: 30000 }),
  markRead: (channelId) => api.post(`/api/chat/channels/${channelId}/read`, {}, { timeout: 30000 }),
};

export const activityAPI = {
  getHistory: (params = {}) => api.get("/api/activity", { params }),
};

export const mailAPI = {
  getThreads: (params = {}) => api.get("/api/mail/threads", { params, timeout: 30000 }),
  getThread: (threadId) => api.get(`/api/mail/threads/${threadId}`, { timeout: 30000 }),
  createThread: (data) => api.post("/api/mail/threads", data, { timeout: 30000 }),
  replyToThread: (threadId, data) => api.post(`/api/mail/threads/${threadId}/reply`, data, { timeout: 30000 }),
  forwardMessage: (messageId, data) => api.post(`/api/mail/messages/${messageId}/forward`, data, { timeout: 30000 }),
  updateThreadState: (threadId, data) => api.patch(`/api/mail/threads/${threadId}/read-state`, data, { timeout: 30000 }),
  uploadAttachments: (attachments) => api.post("/api/mail/attachments", { attachments }, { timeout: 30000 }),
};

export const reportsAPI = {
  getReports: (params = {}) => api.get("/api/reports", { params, timeout: 30000 }),
  createReport: (data) => api.post("/api/reports", data, { timeout: 30000 }),
  updateReport: (id, data) => api.put(`/api/reports/${id}`, data, { timeout: 30000 }),
  deleteReport: (id) => api.delete(`/api/reports/${id}`),
  handoffReport: (id) => api.post(`/api/reports/${id}/handoff`, {}, { timeout: 30000 }),
  submitReport: (id) => api.post(`/api/reports/${id}/submit`, {}, { timeout: 30000 }),
  reviewReport: (id, data) => api.post(`/api/reports/${id}/review`, data, { timeout: 30000 }),
};

export const docsAPI = {
  getDocs: (params = {}) => api.get("/api/docs", { params }),
  getDoc: (id) => api.get(`/api/docs/${id}`),
  createDoc: (data) => api.post("/api/docs", data),
  updateDoc: (id, data) => api.put(`/api/docs/${id}`, data),
  deleteDoc: (id) => api.delete(`/api/docs/${id}`),
};

export const meetingsAPI = {
  getMeetings: (params = {}) => api.get("/api/meetings", { params }),
  getMeeting: (id) => api.get(`/api/meetings/${id}`),
  updateMeeting: (id, data) => api.put(`/api/meetings/${id}`, data),
  getConferenceNotes: (conferenceId) => api.get(`/api/meetings/conference/${encodeURIComponent(conferenceId)}/notes`),
  getConferenceHistory: (params = {}) => api.get("/api/meetings/conference-history", { params }),
  endConference: (conferenceId) => api.post(`/api/meetings/conference/${encodeURIComponent(conferenceId)}/end`),
  updateConferenceNotes: (conferenceId, data) => api.put(`/api/meetings/conference/${encodeURIComponent(conferenceId)}/notes`, data),
  summarizeMeeting: (meetingId, data = {}) => api.post("/api/meetings/summarize", { ...data, ...(meetingId ? { meetingId } : {}) }),
};

export const aiAPI = {
  getSuggestions: (surface, context = {}) => api.post("/api/ai/suggestions", { surface, context }),
  createAssignmentDraft: (prompt, context = {}) => api.post("/api/ai/research/assignment", { prompt, context }),
};

export const leaveRequestsAPI = {
  getMyRequests: () => api.get("/api/leave-requests/my"),
  createRequest: (data) => api.post("/api/leave-requests", data),
  getTeamRequests: (teamId) => api.get(`/api/leave-requests/team/${teamId}`),
  reviewRequest: (id, data) => api.put(`/api/leave-requests/${id}/review`, data),
  getStatus: (teamId, params) => api.get(`/api/leave-requests/status/${teamId}`, { params }),
};

export const performanceAPI = {
  getMyPerformance: () => api.get("/api/performance/my"),
  getTeamPerformance: (teamId) => api.get(`/api/performance/team/${teamId}`),
};

export default api;
